import { LineRef, lineKey } from '@/models/lineKey';

export type { LineRef };

/** A billable line available for distribution in the builder. */
export interface OrderedService {
  lineRef: LineRef;
  serviceId: number;
  name: string;
  saleValue: number;
  cogs: number;
  /** EUR-normalized COGS (from the COGS endpoint), for cross-currency totals. */
  cogsEur?: number;
  currency: string;
  cogsEstimated?: boolean;
  blockTitle?: string | null;
  cpName?: string | null;
}

/** Editable draft installment used by the builder (maps 1:1 to a split_plan_item). */
export interface DraftInstallment {
  /** Stable local key (negative for unsaved rows, plan_item id once persisted). */
  key: string;
  seq: number;
  currency: string;
  paymentMethod: string;
  payerId: number | null;
  /** Independent billed amount. Null falls back to the covered-lines sum. */
  amount: number | null;
  /** Covered billable lines — informational labels, not the source of the amount. */
  lines: LineRef[];
  /** BizDev's description of the installment. The only text the customer gets about
   *  it, and the only content an installment covering no services has at all. */
  note?: string | null;
}

export interface SplitDerived {
  orderedTotal: number;
  cogsTotal: number;
  /** Sale value covered by installment, keyed by DraftInstallment.key. */
  saleByInst: Record<string, number>;
  /** COGS covered by installment, keyed by DraftInstallment.key. */
  cogsByInst: Record<string, number>;
  /** COGS covered by the first installment (seq 1). */
  firstInstallmentCogs: number;
  /** Sale value covered by the first installment (seq 1). */
  firstInstallmentSale: number;
  /** seq-1 installment covers the full order COGS. */
  cogsCovered: boolean;
  assignedCount: number;
  allAssigned: boolean;
  /** Sum of the billed installment amounts (typed, or line-sum fallback). */
  amountsTotal: number;
  /** The installment amounts reconcile with the order total. */
  amountsSumToTotal: boolean;
  /** Hard gate (amounts reconcile with the order total). */
  canSubmit: boolean;
}

/** A money figure as a whole number of cents, so totals compare exactly. */
const minor = (n: number) => Math.round(n * 100);

export function deriveSplit(
  installments: DraftInstallment[],
  services: OrderedService[],
  /**
   * What the order is worth, in minor units of the plan's base currency, as valued by
   * the server. Summing `saleValue` here instead adds prices in different currencies
   * together on a mixed-currency order - and offers routinely mix them, because an offer
   * can carry proposals priced in different currencies.
   */
  orderNetMinor?: number | null,
): SplitDerived {
  const orderedTotalMinor =
    orderNetMinor ?? services.reduce((s, x) => s + minor(x.saleValue), 0);
  const orderedTotal = orderedTotalMinor / 100;
  // COGS is normalised the same way; `cogsEur` is supplied for exactly this and was
  // being ignored in favour of adding raw amounts across currencies.
  const cogsTotal = services.reduce((s, x) => s + (x.cogsEur ?? x.cogs), 0);

  const byLineKey = new Map(services.map((s) => [lineKey(s.lineRef), s]));

  const saleByInst: Record<string, number> = {};
  const cogsByInst: Record<string, number> = {};
  const seen = new Set<string>();
  let assignedCount = 0;

  for (const inst of installments) {
    // Line-sum is the fallback billed value + the basis for COGS attribution.
    let lineSum = 0;
    cogsByInst[inst.key] = 0;
    for (const ref of inst.lines) {
      const lk = lineKey(ref);
      const svc = byLineKey.get(lk);
      if (svc && !seen.has(lk)) {
        seen.add(lk);
        lineSum += svc.saleValue;
        cogsByInst[inst.key] += svc.cogs;
        assignedCount++;
      }
    }
    // The billed amount is the typed value; null legacy drafts fall back to the sum.
    saleByInst[inst.key] = inst.amount != null ? inst.amount : lineSum;
  }

  const sorted = [...installments].sort((a, b) => a.seq - b.seq);
  const first = sorted[0];
  const firstInstallmentCogs = first ? (cogsByInst[first.key] ?? 0) : 0;
  const firstInstallmentSale = first ? (saleByInst[first.key] ?? 0) : 0;
  const cogsCovered =
    cogsTotal <= 0.01 || minor(firstInstallmentCogs) + 1 >= minor(cogsTotal);

  const allAssigned = services.length > 0 && assignedCount === services.length;

  const amountsTotal = Object.values(saleByInst).reduce((s, v) => s + v, 0);
  // Exact, in minor units. The old floating tolerance of 0.01 let a schedule drift from
  // its order by a cent before a single invoice had been raised, and the server now
  // refuses anything that does not reconcile exactly - so accepting it here would only
  // offer the user a Submit button that fails.
  const amountsSumToTotal =
    installments.length > 0 &&
    Object.values(saleByInst).reduce((s, v) => s + minor(v), 0) ===
      orderedTotalMinor;

  return {
    orderedTotal,
    cogsTotal,
    saleByInst,
    cogsByInst,
    firstInstallmentCogs,
    firstInstallmentSale,
    cogsCovered,
    assignedCount,
    allAssigned,
    amountsTotal,
    amountsSumToTotal,
    canSubmit: amountsSumToTotal,
  };
}

/**
 * A figure used for reconciliation must be shown as it will be reconciled. Rounding to
 * whole units here displayed 100.49 as "100" while the schedule was checked against the
 * cents.
 */
export const money = (n: number, currency: string) =>
  `${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency}`;

/**
 * Set one installment to `pct` of the order total, parking the rounding residual on
 * the last other installment so the schedule still reconciles exactly.
 *
 * Percentages of a total whose cents do not divide evenly (any discounted order can
 * land on one) round each row up independently: 50/50 of 53,622.75 gives 26,811.38
 * twice, a cent over the order, and submission is gated on exact reconciliation - so
 * the residual has to land somewhere or the Submit button is simply unreachable.
 */
export function applyPercentage(
  installments: DraftInstallment[],
  amounts: Record<string, number>,
  key: string,
  pct: number,
  orderedTotal: number,
): DraftInstallment[] {
  const totalMinor = minor(orderedTotal);
  const targetMinor = Math.max(0, Math.round((totalMinor * pct) / 100));

  const sorted = [...installments].sort((a, b) => a.seq - b.seq);
  const absorber = [...sorted].reverse().find((i) => i.key !== key);
  if (!absorber) {
    return installments.map((i) =>
      i.key === key ? { ...i, amount: targetMinor / 100 } : i,
    );
  }

  const othersMinor = sorted
    .filter((i) => i.key !== key && i.key !== absorber.key)
    .reduce((s, i) => s + minor(amounts[i.key] ?? 0), 0);
  const absorberMinor = Math.max(0, totalMinor - targetMinor - othersMinor);

  return installments.map((i) => {
    if (i.key === key) return { ...i, amount: targetMinor / 100 };
    if (i.key === absorber.key) return { ...i, amount: absorberMinor / 100 };
    return i;
  });
}
